import React, { useMemo, useCallback, useEffect } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MarkerType,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./flowline-graph.css";
import QueueNode from "./QueueNode";
import DlqNode from "./DlqNode";
import LambdaNode from "./LambdaNode";
import { edgeTypes } from "./PipelineEdges";
import { sqsToColor, dlqToColor, lambdaToColor } from "./pipelineConfig";
import { getQueueValuesAtTime, getGlobalPeakSent } from "./timelineUtils";
import { getPipelineLayout, isLambdaStage, getLambdaFeedTargets } from "./layout";

const nodeTypes = { queueNode: QueueNode, dlqNode: DlqNode, lambdaNode: LambdaNode };

const pipelineLayout = getPipelineLayout();

const FIT_VIEW_OPTIONS = {
  padding: 0.06,
  minZoom: 0.35,
  maxZoom: 2.5,
  duration: 150,
};

function stageKeyFromNodeId(nodeId) {
  return nodeId.replace(/-dlq$/, "");
}

function FitViewOnChange({ nodeCount }) {
  const { fitView } = useReactFlow();

  useEffect(() => {
    if (!nodeCount) return;
    const frame = requestAnimationFrame(() => {
      fitView(FIT_VIEW_OPTIONS);
    });
    return () => cancelAnimationFrame(frame);
  }, [nodeCount, fitView]);

  return null;
}

const PipelineGraphInner = ({
  pipeline,
  metrics,
  selectedStageKey,
  onSelectStage,
  selectedTimestamp,
}) => {
  const maxPeakSent = useMemo(
    () => getGlobalPeakSent(metrics, pipeline),
    [metrics, pipeline]
  );

  const valuesAtTime = useMemo(
    () => getQueueValuesAtTime(metrics, pipeline, selectedTimestamp),
    [metrics, pipeline, selectedTimestamp]
  );

  const lambdaFeedTargets = useMemo(
    () => getLambdaFeedTargets(pipeline),
    [pipeline]
  );

  const stageByKey = useMemo(() => {
    const map = new Map();
    for (const stage of pipeline?.stages ?? []) {
      map.set(stage.key, stage);
    }
    return map;
  }, [pipeline]);

  const nodes = useMemo(() => {
    if (!pipeline?.stages) return [];

    const graphNodes = [];
    for (const stage of pipeline.stages) {
      const atTime = valuesAtTime[stage.key] ?? {
        sent: 0,
        deleted: 0,
        dlqVisible: 0,
        invocations: 0,
      };
      const position =
        pipelineLayout.positions[stage.key] ??
        pipeline.positions?.[stage.key] ??
        { x: 0, y: 0 };
      const isSelected = selectedStageKey === stage.key;

      if (isLambdaStage(stage)) {
        graphNodes.push({
          id: stage.key,
          type: "lambdaNode",
          position,
          data: {
            shortName: stage.shortName,
            invocations: atTime.invocations,
            colors: lambdaToColor(atTime.invocations, maxPeakSent),
          },
          selected: isSelected,
        });
        continue;
      }

      const mainActivity = Math.max(atTime.sent, atTime.deleted);

      graphNodes.push({
        id: stage.key,
        type: "queueNode",
        position,
        data: {
          stageKey: stage.key,
          shortName: stage.shortName,
          sent: atTime.sent,
          deleted: atTime.deleted,
          hasLambdaFeed: lambdaFeedTargets.has(stage.key),
          hasDlq: Boolean(stage.dlq),
          dlqVisible: atTime.dlqVisible,
          dlqColors: dlqToColor(atTime.dlqVisible),
          colors: sqsToColor(mainActivity, maxPeakSent),
        },
        selected: isSelected,
      });
    }

    return graphNodes;
  }, [pipeline, valuesAtTime, maxPeakSent, selectedStageKey, lambdaFeedTargets]);

  const edges = useMemo(() => {
    if (!pipeline?.edges || !pipeline?.stages) return [];

    const pipelineEdges = pipeline.edges.map((edge) => {
      const sourceStage = stageByKey.get(edge.source);
      const isLambdaFeed = isLambdaStage(sourceStage);
      const isFanOut = edge.source === "E";
      return {
        id: `${edge.source}-${edge.target}`,
        source: edge.source,
        target: edge.target,
        sourceHandle: "pipeline-out",
        targetHandle: isLambdaFeed ? "from-lambda" : "pipeline-in",
        type: isFanOut ? "fanoutEdge" : isLambdaFeed ? "lambdaFeedEdge" : "mainEdge",
        animated: false,
        markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
        style: { stroke: "#9ca3af", strokeWidth: 2 },
      };
    });

    return pipelineEdges;
  }, [pipeline, stageByKey]);

  const onNodeClick = useCallback(
    (_event, node) => {
      const stageKey = stageKeyFromNodeId(node.id);
      onSelectStage(stageKey === selectedStageKey ? null : stageKey);
    },
    [onSelectStage, selectedStageKey]
  );

  const onInit = useCallback((instance) => {
    instance.fitView(FIT_VIEW_OPTIONS);
  }, []);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodeClick={onNodeClick}
      onInit={onInit}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable
      panOnScroll
      minZoom={0.3}
      maxZoom={2.5}
      proOptions={{ hideAttribution: true }}
    >
      <FitViewOnChange nodeCount={nodes.length} />
      <Background gap={16} size={1} color="#e5e7eb" />
      <Controls showInteractive={false} position="bottom-left" />
    </ReactFlow>
  );
};

const PipelineGraph = ({
  pipeline,
  metrics,
  selectedStageKey,
  onSelectStage,
  selectedTimestamp,
}) => {
  if (!pipeline) {
    return (
      <div className="h-[420px] flex items-center justify-center text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
        Loading pipeline…
      </div>
    );
  }

  return (
    <div
      className="flowline-graph w-full border border-gray-200 rounded-t-lg bg-gray-50"
      style={{ height: pipelineLayout.graphHeight }}
    >
      <ReactFlowProvider>
        <PipelineGraphInner
          pipeline={pipeline}
          metrics={metrics}
          selectedStageKey={selectedStageKey}
          onSelectStage={onSelectStage}
          selectedTimestamp={selectedTimestamp}
        />
      </ReactFlowProvider>
    </div>
  );
};

export default PipelineGraph;
